<script setup lang="ts">
import { type Component, computed, useSlots } from 'vue'

import { slotRenders } from '../slot-renders'
import {
  BADGE_NEUTRAL_VALUE_CLASSES,
  BADGE_PLAIN_TONES,
  BADGE_TONE_CLASSES,
  type BadgeIndicator,
  type BadgeSize,
  type BadgeTone,
} from './badge-types'

const props = withDefaults(
  defineProps<{
    /** Semantic colour. `neutral` is a grey fill; the accents are a soft tint of their own hue; `inverted` is solid ink; `quiet` is bare text; `outline` is a dashed rim for a slot nothing fills yet. */
    tone?: BadgeTone
    /** `sm` (default) labels a thing · `md` shows a value the founder owns, such as a customer phrase or a region. */
    size?: BadgeSize
    /** A filled (bold) Solar icon before the label; the badge sets its size and colour. */
    icon?: Component
    /** A 6px dot before the label: `dot` at rest, `pulse` while the work is still in flight. */
    indicator?: BadgeIndicator
    /** A count or short note after the label, such as `3` or "3 countries". */
    meta?: string | number
    /** Renders a fixed-size circular tile for a lone glyph instead of the padded badge. */
    iconOnly?: boolean
    /** Rounds the ends into a pill, for a chip that rides in running text or counts an answer's sources. */
    pill?: boolean
    /** Lets the label wrap onto more than one line and never outgrow its container, for phrases of unpredictable length. */
    wrap?: boolean
  }>(),
  {
    tone: 'neutral',
    size: 'sm',
    icon: undefined,
    indicator: undefined,
    meta: undefined,
    iconOnly: false,
    pill: false,
    wrap: false,
  },
)

const slots = useSlots()

const hasMeta = computed(() => props.meta !== undefined && props.meta !== '')

const toneClass = computed(() =>
  props.size === 'md' && props.tone === 'neutral'
    ? BADGE_NEUTRAL_VALUE_CLASSES
    : BADGE_TONE_CLASSES[props.tone],
)

function paddingClass(): string {
  const block = props.size === 'md' ? 'py-2' : 'py-1'
  if (props.tone === 'quiet') return block
  const lead = Boolean(props.icon) || Boolean(props.indicator) || slotRenders(slots.leading)
  const text = hasMeta.value || slotRenders(slots.default)
  if (!lead) return `${block} px-4`
  return text ? `${block} pr-4 pl-2` : `${block} px-2`
}

const metaClass = computed(() =>
  BADGE_PLAIN_TONES.has(props.tone) ? 'text-text-tertiary' : undefined,
)
</script>

<template>
  <span
    :class="[
      'inline-flex w-fit select-none items-center align-middle transition-[background-color,color] duration-moderate ease-soft',
      size === 'md' ? 'gap-2 text-body-sm' : 'gap-1 text-caption',
      wrap ? 'max-w-full text-left break-words' : 'whitespace-nowrap',
      iconOnly
        ? 'size-16 shrink-0 justify-center rounded-full'
        : [
            size === 'md' ? 'icon-sm' : 'icon-xs',
            pill ? 'rounded-full' : 'rounded-4',
            paddingClass(),
          ],
      toneClass,
    ]"
  >
    <span
      v-if="indicator"
      aria-hidden="true"
      :class="[
        'mx-1 size-(--size-badge-dot) shrink-0 rounded-full bg-current',
        indicator === 'pulse' ? 'motion-safe:animate-badge-pulse' : '',
      ]"
    />
    <span v-if="slotRenders($slots.leading)" class="inline-flex shrink-0">
      <slot name="leading" />
    </span>
    <component :is="icon" v-else-if="icon" class="shrink-0" aria-hidden="true" />
    <slot />
    <span v-if="hasMeta" class="text-caption tabular-nums" :class="metaClass">{{ meta }}</span>
  </span>
</template>
